import React, { useState, useEffect, useCallback, useRef } from "react"
import { Navigate, useNavigate } from "react-router-dom"
import styled from "styled-components"
import "@fontsource/montserrat"
import { useAuthProvider } from "../../../Providers/AuthProvider.js"
import { FRONTEND_LINKS } from '../../../Constants/FrontendLinks'
import bgImg from "./bgImg.png"
import darkLogoImg from "./moberg-logo-dark.png"
import lightLogoImg from "./moberg-logo-light.png"
import { useMobergTheme } from "../../../Providers/MobergThemeProvider"
import ErrorPopupModal from "../../../Components/ErrorPopupModal/ErrorPopupModal.js"
import SafariUnsupportedModal from "../../../Components/SafariUnsupportedModal/SafariUnsupportedModal.js"
import { useModalProvider } from "../../../Providers/ModalProvider"
import { Deployment, useEnvironmentVariablesProvider } from "../../../Providers/EnvironmentVariablesProvider"
import { useBackendLinksProvider } from '../../../Providers/BackendLinksProvider'
import { useEndpointProvider } from '../../../Providers/EndpointProvider'
import { SOFTWARE_VERSION } from '../../../Version.js'
import { MobergFontFamily } from "../../../Components/MobergFont/MobergFont"
import { MdVisibility, MdVisibilityOff, MdError } from 'react-icons/md'
import ZoomResolutionChecker from "../../../Components/ZoomResolutionChecker"
import { MobergLogoSpinner } from "../../../Components/MobergLogoSpinner/MobergLogoSpinner"
import DisplayWarningToast from "../../../Components/DisplayWarningToast/DisplayWarningToast"
import LoginCarousel from "../../../Components/LoginCarousel/LoginCarousel"
import { useBrowserInfo } from "../../../Hooks/useBrowserInfo"
import { useDisplayOptimality } from "../../../Hooks/useDisplayOptimality"

import carouselImg1 from "./carouselImg/Invitation invalid.png";
import carouselImg2 from "./carouselImg/Invitation invalid-1.png";
import carouselImg3 from "./carouselImg/Invitation invalid-2.png";


// Reject any redirect_uri whose origin isn't the Analytics Studio host —
// an attacker-controlled one would otherwise receive a live OAuth code.
function isAllowedRedirect(redirectUri, sandboxUrl) {
	if (!redirectUri || !sandboxUrl) {
		return false  // fail closed — can't validate the target
	}
	try {
		return new URL(redirectUri).origin === new URL(sandboxUrl).origin
	} catch {
		return false  // fail closed — redirect_uri isn't a valid URL
	}
}


function redirectToOAuthClient(redirectUri, state, code) {
	const url = new URL(redirectUri)
	url.searchParams.set("state", state)
	url.searchParams.set("code", code)
	window.location.replace(url.toString())
}

// JupyterHub's OAuth redirect carries these three params; their presence
// marks this page load as an authorize request, not a normal login visit.
function isOAuthRequest() {
	const params = new URLSearchParams(window.location.search)
	return Boolean(params.get("redirect_uri"))
		&& Boolean(params.get("state"))
		&& Boolean(params.get("client_id"))
}


function Login({ setNavPage }) {
	let navigate = useNavigate()

	const authProvider = useAuthProvider()
	const { createModal } = useModalProvider()
	const { colorScheme } = useMobergTheme()
	const logoImg = colorScheme === "dark" ? lightLogoImg : darkLogoImg
	const [loading, setLoading] = useState(false)
	const [loadingText, setLoadingText] = useState("Loading workspace...")
	// True while a silent OAuth handoff is in flight — show a spinner, not
	// the form. Seeded from the URL so the form never flashes on arrival.
	const [silentSSO, setSilentSSO] = useState(() => isOAuthRequest())
	const silentSSOAttempted = useRef(false)
	const [email, setEmail] = useState()
	const [password, setPassword] = useState()
	const [showPassword, setShowPassword] = useState(false)
	const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
	const [showZoomChecker, setShowZoomChecker] = useState(false)
	const [showDisplayWarning, setShowDisplayWarning] = useState(false)
	const [emailError, setEmailError] = useState("")
	const [passwordError, setPasswordError] = useState("")
	
	// Use custom hooks for browser detection and display optimality
	const browserInfo = useBrowserInfo()
	const { isOptimal: isDisplayOptimal } = useDisplayOptimality()

	useEffect(() => {
		if (browserInfo.isSafari) {
			createModal(<SafariUnsupportedModal />)
		}
	}, [browserInfo.isSafari, createModal])

	const { deployment, environmentVariables } = useEnvironmentVariablesProvider()
	const isClinical = deployment === Deployment.CLINICAL
	const { SANDBOX_URL } = environmentVariables

	const [, setLoginUpdate] = useState([])
	const { LINKS } = useBackendLinksProvider()
	const endpointProvider = useEndpointProvider()

	// Carousel configuration
	const carouselImages = [
		{ src: carouselImg1, alt: 'CPPopt Autoregulation Dashboard', title: 'CPPopt Autoregulation Dashboard' },
		{ src: carouselImg2, alt: 'Moberg x ICM+ CPPopt Comparison', title: 'Moberg x ICM+ CPPopt Comparison' },
		{ src: carouselImg3, alt: 'MAPopt Autoregulation Dashboard', title: 'MAPopt Autoregulation Dashboard' }
	];

	function updateLogin() {
		setLoginUpdate([])
	}

	// Email validation function
	const validateEmail = (email) => {
		if (!email || email.trim() === '') {
			return "Email is required"
		}

		// Check length
		if (email.length > 254) {
			return "Email must be no longer than 254 characters"
		}

		// Check for exactly one @ symbol
		const atCount = (email.match(/@/g) || []).length
		if (atCount === 0) {
			return "Email must contain an @ symbol"
		}
		if (atCount > 1) {
			return "Email can only contain one @ symbol"
		}

		// Check email format using regex
		const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
		if (!emailRegex.test(email)) {
			return "Please enter a valid email address"
		}

		return ""
	}

	// Clear errors when email changes
	const handleEmailChange = (e) => {
		setEmail(e.target.value)
		setEmailError("")
	}

	// Clear errors when password changes
	const handlePasswordChange = (e) => {
		setPassword(e.target.value)
		setPasswordError("")
	}

	// Check display area and set up event listeners
	useEffect(() => {
		setNavPage(false)
		window.addEventListener("resize", updateLogin)
		
		return () => {
			window.removeEventListener("resize", updateLogin)
		}
	}, [setNavPage])

	// Update display warning based on display optimality
	useEffect(() => {
		setShowDisplayWarning(!isDisplayOptimal)
	}, [isDisplayOptimal])

	const handleForgotPassword = (e) => {
		if (e) {
			e.preventDefault()
		}
		navigate(FRONTEND_LINKS.SUBPAGES.FORGOT_PASSWORD.MAIN)
	}

	const handleConfigureDisplay = () => {
		setShowDisplayWarning(false)
		setShowZoomChecker(true)
	}



	const handleLogin = useCallback(async (e) => {
		if (e?.preventDefault) {
			e.preventDefault()
		}

		if (browserInfo.isSafari) {
			return
		}

		let _email = document.getElementById('emailInput').value
		let _password = document.getElementById('passwordInput').value

		// Validate both fields upfront so errors for all empty fields show at once
		const emailValidationError = validateEmail(_email)
		const passwordValidationError = (!_password || _password.trim() === '') ? "Password is required" : ""

		if (emailValidationError) setEmailError(emailValidationError)
		if (passwordValidationError) setPasswordError(passwordValidationError)
		if (emailValidationError || passwordValidationError) return

		setLoading(true)

		const isOAuth = isOAuthRequest()
		const params = new URLSearchParams(window.location.search)


		if (isOAuth) {
			if (!isAllowedRedirect(params.get("redirect_uri"), SANDBOX_URL)) {
				setLoading(false)
				createModal(<ErrorPopupModal
					title={"There was a problem logging in"}
					errorMessage={"This login link points to an unrecognized destination."}
					onClose={() => setIsErrorModalOpen(false) }
				/>)
				return
			}
			setLoadingText("Loading Jupyterhub...")
			let loginSuccess = true
			let response = {}
			try {
				response = await endpointProvider.post(LINKS.LOGIN.OAUTH_LOGIN, {
					"email": _email,
					"password": _password,
					"redirect_uri": params.get("redirect_uri")
				})
			}
			catch (err) {
				createModal(<ErrorPopupModal
					title={"There was a problem logging in"}
					errorMessage={err}
					onClose={() => { setLoading(false); setIsErrorModalOpen(false) }}
				/>)
				return
			}
			if (!loginSuccess) return
			redirectToOAuthClient(params.get("redirect_uri"), params.get("state"), response["code"])
			return
		}
		else {
			setLoadingText("Loading workspace...")
		}
		authProvider.login(_email, _password).then((data) => {
			setLoading(false)
			setNavPage(true)

            if(isClinical){
				navigate(FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN, { state: { admission_status: "admitted" } })
            }
            else{
                navigate(FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN)
            }

		}).catch((e) => {
			setIsErrorModalOpen(true)
			createModal(<ErrorPopupModal
				title={'There was a problem logging in'}
				errorMessage={`${e}`}
				onClose={() => { setLoading(false); setIsErrorModalOpen(false) }}
			/>)
		})
	}, [endpointProvider, LINKS.LOGIN.OAUTH_LOGIN, authProvider, createModal, isClinical, navigate, setLoading, setIsErrorModalOpen, setNavPage, browserInfo.isSafari, SANDBOX_URL])

	const enterLogin = useCallback((event) => {
		if (event.keyCode === 13) {
			handleLogin()
		}
	}, [handleLogin])

	useEffect(() => {
		if (!isErrorModalOpen) {
			document.addEventListener("keydown", enterLogin)
			return () => {
				document.removeEventListener("keydown", enterLogin)
			}
		}
	}, [enterLogin, isErrorModalOpen])

	// Silent SSO: JupyterHub redirected here to authorize. If a dashboard
	// session already exists, mint an OAuth code from it and bounce straight
	// back — no form. Otherwise fall through to the form, whose handleLogin
	// handles the OAuth submit.
	useEffect(() => {
		if (!isOAuthRequest()) { setSilentSSO(false); return }
		if (!authProvider.userIsLoggedIn) { setSilentSSO(false); return }
		if (silentSSOAttempted.current) { return }

		const params = new URLSearchParams(window.location.search)
		// Don't mint a code for an untrusted redirect; show the form instead.
		if (!isAllowedRedirect(params.get("redirect_uri"), SANDBOX_URL)) {
			setSilentSSO(false)
			return
		}
		silentSSOAttempted.current = true

		setLoadingText("Loading Jupyterhub...")
		endpointProvider.post(LINKS.LOGIN.OAUTH_AUTHORIZE, { "redirect_uri": params.get("redirect_uri") })
			.then(response => {
				redirectToOAuthClient(params.get("redirect_uri"), params.get("state"), response["code"])
			})
			.catch(() => {
				// Stale session / rejected token — drop back to the form.
				setSilentSSO(false)
			})
	}, [authProvider.userIsLoggedIn, endpointProvider, LINKS.LOGIN.OAUTH_AUTHORIZE, SANDBOX_URL])

	// If we land on the login page while a session is already active
	// (e.g. opened a new tab in a browser that's logged in, or hit
	// browser-back after signing in), bounce straight into the app
	// instead of showing an empty login form. Placed below all hooks
	// to respect the Rules of Hooks when userIsLoggedIn flips after
	// a successful login submit.
	if (authProvider.userIsLoggedIn && !isOAuthRequest()) {
		return <Navigate to={FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN} replace />
	}



	function buildTitleAndInfo() {

		return (
			<div style={{ width: "90%", height: "100%", paddingTop: "32px", paddingBottom: "32px", margin: "0 auto", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
				<Container3 style={{ display: "flex", flexDirection: "column" }}>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexDirection: "row", width: "100%" }}>
						<h2 style={{ fontFamily: MobergFontFamily.HEADER, fontStyle: "normal", fontWeight: "700", fontSize: "20px", lineHeight: "24px", color: "var(--color-text-primary)" }}>Latest updates</h2>
						<div style={{ fontFamily: MobergFontFamily.HEADER, fontSize: "14px", color: "var(--text-secondary-color)", textDecoration: "underline" }}><a href="https://moberganalytics.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline", color: "inherit" }}>MCP v{SOFTWARE_VERSION}</a></div>
					</div>
					<p style={{ fontSize: "16px", lineHeight: "24px", color: "var(--color-text-primary)" }}>Our focus this quarter has been on enhancing system performance and user experience. We've implemented faster data processing, improved near real-time monitoring capabilities, and optimized the overall platform stability for better user experience. </p>
					<LearnMoreButton onClick={() => window.open("https://moberganalytics.com", "_blank")}>Learn more</LearnMoreButton>
				</Container3>
				<CarouselContainer>
					<LoginCarousel 
						images={carouselImages}
						autoPlay={true}
						autoPlayInterval={4000}
						showNavigation={true}
						showPagination={true}
						showTitle={true}
					/>
				</CarouselContainer>
			</div>
		)
	}


	if (loading || silentSSO) {
		return (
			<LoadingWrapper>
				<LoadingContainer>
					<MobergLogoSpinner />
					<LoadingTitle>{loadingText}</LoadingTitle>
					<LoadingSubtitle>Please wait while we load your workspace.</LoadingSubtitle>
				</LoadingContainer>
			</LoadingWrapper>
		)
	}

	if (isClinical) {
		return (
			<>
				<ClinicalWrapper>
					<CenteredLoginBox>
						<LoginBox style={{ boxShadow: "var(--modal-box-shadow)", background: "var(--color-surface-panel)", borderRadius: 4, minWidth: 350, padding: 32 }}>
							<TopSection style={{ alignItems: "flex-start", paddingTop: 0 }}>
								<div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '32px', flexDirection: "column", alignSelf: "flex-start", width: "100%" }}>
									<img src={logoImg} alt="Moberg Analytics" style={{ width: '90px', alignSelf: "flex-start" }} />
									<LoginHeading style={{ fontFamily: "Montserrat", fontWeight: 800, letterSpacing: 1, marginBottom: 0, fontSize: "24px" }}>MOBERG CLINICAL PLATFORM</LoginHeading>
								</div>
														<LoginSubheading style={{ fontFamily: MobergFontFamily.REGULAR, letterSpacing: 1, fontSize: "24px", margin: 0, marginBottom: 32, alignSelf: "flex-start" }}>Sign in</LoginSubheading>
							</TopSection>
							<CenterSection>
								<LoginForm>
									<LoginInputBox>
										<LoginLabel htmlFor="emailInput">Email</LoginLabel>
										<input id="emailInput" type="email" placeholder="Email" value={email || ""} onChange={handleEmailChange} autoComplete="username" />
										{emailError && <ErrorText><MdError size={16} /> {emailError}</ErrorText>}
									</LoginInputBox>
									<LoginInputBox>
										<LoginLabel htmlFor="passwordInput">Password</LoginLabel>
										<div style={{ flexDirection: "row", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
											<input id="passwordInput" type={showPassword ? "text" : "password"} placeholder="Password" value={password || ""} onChange={handlePasswordChange} autoComplete="current-password" />
											<ShowPasswordIcon onClick={() => setShowPassword(!showPassword)}>
												{showPassword ? <MdVisibilityOff size={22} /> : <MdVisibility size={22} />}
											</ShowPasswordIcon>
										</div>
										{passwordError && <ErrorText><MdError size={16} /> {passwordError}</ErrorText>}
									</LoginInputBox>

								</LoginForm>
							</CenterSection>
							<BottomSection>
								<LoginButton type="button" onClick={handleLogin} disabled={browserInfo.isSafari}>Sign in</LoginButton>
								<ForgotPasswordLink onClick={handleForgotPassword}>Forgot password?</ForgotPasswordLink>
								<LoginFooter>
									<div><a href="https://moberganalytics.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline", color: "inherit" }}>MCP v{SOFTWARE_VERSION}</a></div>
									<div>Copyright © {new Date().getFullYear()} Moberg Analytics, Inc. All rights reserved. <br /> <a href="https://moberganalytics.com/privacy/" target="_blank" rel="noreferrer">Privacy</a></div>
								</LoginFooter>
							</BottomSection>
						</LoginBox>
					</CenteredLoginBox>
				</ClinicalWrapper>
				
				<DisplayWarningToast
					isVisible={showDisplayWarning && !browserInfo.isSafari}
					onConfigure={handleConfigureDisplay}
					showCloseButton={false}
					isSafari={browserInfo.isSafari}
				/>
				
				<ZoomResolutionChecker 
					isVisible={showZoomChecker}
					onClose={() => setShowZoomChecker(false)}
				/>
			</>
		)
	}

	return (
		<>
			<Container1>
				<Wrapper style={{ flex: 0.65, flexDirection: "column" }}>
					{buildTitleAndInfo()}
				</Wrapper>
				<RightSection style={{ flex: 0.35 }}>
					<LoginBox>
						<TopSection>
							<img src={logoImg} alt="Moberg Analytics" style={{ width: 108, marginBottom: 32 }} />
							<LoginHeading style={{ fontFamily: "Montserrat", fontWeight: 800, fontSize: "30px", letterSpacing: 1, marginBottom: 16 }}>MOBERG CLOUD PLATFORM</LoginHeading>
						</TopSection>
											<CenterSection>
						<LoginSubheading style={{ fontFamily: MobergFontFamily.REGULAR, fontSize: "24px", letterSpacing: 1, margin: 0, marginBottom: 32, alignSelf: "flex-start" }}>Sign in</LoginSubheading>
						<LoginForm>
								<LoginInputBox>
									<LoginLabel htmlFor="emailInput">Email</LoginLabel>
									<input id="emailInput" type="email" placeholder="Email" value={email || ""} onChange={handleEmailChange} autoComplete="username" />
									{emailError && <ErrorText><MdError size={16} /> {emailError}</ErrorText>}
								</LoginInputBox>
								<LoginInputBox>
									<LoginLabel htmlFor="passwordInput">Password</LoginLabel>
									<div style={{ flexDirection: "row", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
										<input id="passwordInput" type={showPassword ? "text" : "password"} placeholder="Password" value={password || ""} onChange={handlePasswordChange} autoComplete="current-password" />
										<ShowPasswordIcon onClick={() => setShowPassword(!showPassword)}>
											{showPassword ? <MdVisibilityOff size={22} /> : <MdVisibility size={22} />}
										</ShowPasswordIcon>
									</div>
									{passwordError && <ErrorText><MdError size={16} /> {passwordError}</ErrorText>}
								</LoginInputBox>

							</LoginForm>
							<LoginButton type="button" onClick={handleLogin} style={{ marginTop: 32 }} disabled={browserInfo.isSafari}>Sign in</LoginButton>
							<ForgotPasswordLink onClick={handleForgotPassword} style={{ marginTop: 24 }}>Forgot password?</ForgotPasswordLink>
						</CenterSection>

						<BottomSection>
							<LoginFooter>
								<div><a href="https://moberganalytics.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline", color: "inherit" }}>MCP v{SOFTWARE_VERSION}</a></div>
								<div>Copyright © {new Date().getFullYear()} Moberg Analytics, Inc. All rights reserved. <br /> <a href="https://moberganalytics.com/privacy/" target="_blank" rel="noreferrer">Privacy</a></div>
							</LoginFooter>
						</BottomSection>
					</LoginBox>
				</RightSection>
			</Container1>
			
			<DisplayWarningToast
				isVisible={showDisplayWarning && !browserInfo.isSafari}
				onConfigure={handleConfigureDisplay}
				showCloseButton={false}
				isSafari={browserInfo.isSafari}
			/>
			
			<ZoomResolutionChecker 
				isVisible={showZoomChecker}
				onClose={() => setShowZoomChecker(false)}
			/>
		</>
	)
}

const Container1 = styled.div`
	background: var(--color-surface-primary);
	position: absolute;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	min-height: 585px;
	display: flex;
	overflow: hidden;
`;

const Wrapper = styled.div`
	background-image: url(${bgImg});
	background-position: center;
	background-size: cover;
	background-repeat: no-repeat;
	background-color: var(--color-surface-primary);

	height: 100%;
	display: flex;
	position: relative;
	min-width: 600px;
	flex: 1;

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0);
		z-index: 0;
		[data-color-scheme="dark"] & {
			background: rgba(0, 0, 0, 0.75);
		}
	}

	& > * {
		position: relative;
		z-index: 1;
	}

	@media (max-width: 1024px) {
		display: none;
	}
	
	/* Ensure proper width utilization on larger screens */
	@media (min-width: 1440px) {
		min-width: 800px;
	}
	
	@media (min-width: 1920px) {
		min-width: 1000px;
	}
	
	@media (min-width: 2560px) {
		min-width: 1200px;
	}
`;

const RightSection = styled.div`

	max-width: 100vw;
	min-width: 0;
	height: 100vh;
	background: var(--color-surface-panel);
	display: flex;
	flex-direction: column;
	box-shadow: var(--modal-box-shadow);
	padding-left: 40px;
	padding-right: 40px;
	@media (max-width: 1024px) {
		flex: 1;
		width: 100%;
		
	}
	@media (max-width: 600px) {
		width: 100vw;
		padding-left: 8px;
		padding-right: 8px;
	}
`;

const LoginBox = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
`;

const LearnMoreButton = styled.button`
width: 100%;
max-width: 200px;
padding: 13px 0;
background: var(--color-accent-interactive);
color: #fff;
font-size: 0.9rem;
font-weight: 600;
font-family: ${MobergFontFamily.HEADER};
border: none;
border-radius: 4px;
cursor: pointer;
margin-top: 0;
transition: filter 0.2s;

[data-color-scheme="dark"] & {
	background: #207DEA;
	color: #fff;
}

&:hover {
	filter: brightness(0.85);
}
`;


const TopSection = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	padding-top: 48px;
	@media (max-width: 600px) {
		align-items: center;
		padding-top: 24px;
		padding-bottom: 16px;
	}
`;

const CenterSection = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 0;
`;

const BottomSection = styled.div`
	width: 100%;
	padding-bottom: 24px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 24px;
	@media (max-width: 600px) {
		padding-bottom: 32px;
	}
`;

const LoginHeading = styled.h1`
	color: var(--color-text-primary);
	font-weight: 700;
	margin: 0 0 8px 0;
	letter-spacing: 1px;
`;

const LoginSubheading = styled.h2`

	color: var(--color-accent-interactive);
	margin:  0 0 24px 0;
	letter-spacing: 1px;
`;

const LoginForm = styled.form`
	width: 100%;
	max-width: 100%;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	gap: 24px;
	@media (max-width: 600px) {
		width: 100%;
		min-width: 0;
		gap: 20px;
	}
`;

const LoginInputBox = styled.div`
	position: relative;
	margin-bottom: 0;
	input {
		width: 100%;
		padding: 12px 40px 12px 12px;
		border: 1.5px solid var(--color-border-default);
		border-radius: 4px;
		font-size: 14px;
		font-family: inherit;
		outline: none;
		transition: border 0.2s;
		background: var(--color-input-bg);
		color: var(--color-text-primary);
		&:focus {
			border-color: var(--color-accent-interactive);
		}
		&::-ms-reveal {
			display: none;
		}
		&:-webkit-autofill,
		&:-webkit-autofill:hover,
		&:-webkit-autofill:focus {
			-webkit-box-shadow: 0 0 0 1000px var(--color-input-bg) inset;
			-webkit-text-fill-color: var(--color-text-primary);
			caret-color: var(--color-text-primary);
			transition: background-color 5000s ease-in-out 0s;
		}
	}
`;

const ErrorText = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	color: var(--color-danger-text);
	font-size: 12px;
	font-weight: 500;
	margin-top: 4px;
	margin-left: 2px;
	font-family: inherit;
	svg {
		flex-shrink: 0;
	}
`;

const LoginLabel = styled.label`
	display: block;
	font-size: 16px;
	color: var(--color-text-primary);
	font-weight: 500;
	margin-bottom: 6px;
	margin-left: 2px;
	font-family: inherit;
`;

const ShowPasswordIcon = styled.span`
	position: absolute;
	right: 14px;
	cursor: pointer;
	font-size: 1.2rem;
	user-select: none;
	display: flex;
	align-items: center;
	color: var(--color-text-primary);
	opacity: 0.5;
`;

const ForgotPasswordLink = styled.a`
	align-self: flex-end;
	color: var(--color-accent-interactive);
	font-size: 14px;
	margin-bottom: 24px;
	cursor: pointer;
	text-decoration: none;
	&:hover {
		text-decoration: underline;
	}
`;

const LoginButton = styled.button`
	width: 100%;
	padding: 12px 0;
	background: var(--color-accent-interactive);
	color: #fff;
	font-size: 16px;
	font-weight: 600;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	margin-top: 32px;
	transition: filter 0.2s;

	[data-color-scheme="dark"] & {
		background: #207DEA;
		color: #fff;
	}

	&:hover:not(:disabled) {
		filter: brightness(0.85);
	}
	&:disabled {
		background: var(--color-border-default);
		cursor: not-allowed;
		opacity: 0.6;
	}
`;

const LoginFooter = styled.div`
	font-size: 12px;
	color: var(--color-text-primary);
	opacity: 0.5;
	text-align: center;
	display: flex;
	flex-direction: column;
	gap: 2px;
	div {
		margin: 0;
	}
	a {
		color: var(--color-accent-interactive);
		opacity: 1;
		text-decoration: none;
		&:hover {
			text-decoration: underline;
		}
	}
`;


const Container3 = styled.div`
	width: 100%;
	height: 250px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	padding: 32px;
	align-items: flex-start;
	background-color: var(--color-surface-panel);
	border-radius: 4px;
	gap: 16px;
	
	/* Responsive adjustments for larger screens */
	@media (min-width: 1440px) {
		padding: 40px;
		height: 280px;
	}
	
	@media (min-width: 1920px) {
		padding: 48px;
		height: 300px;
	}
	
	@media (min-width: 2560px) {
		padding: 56px;
		height: 320px;
	}
	
	@media (max-width: ${({ left }) => `${left + 1210}px`}) {
		display: none;
	}
	
	h2 {


		font-family: ${MobergFontFamily.HEADER};
		font-style: normal;
		font-weight: 700;
		font-size: 24px;


		color: var(--color-text-primary);

		@media (max-width: 900px) {
			display: none;
		}
	}
	h1 {

		width: 700px;
		height: 93px;
		left: ${({ left }) => `${left + 132}px`};
		top: ${({ offset }) => `${offset + 52}px`};
		font-family: ${MobergFontFamily.HEADER};
		font-style: normal;
		font-weight: bold;
		font-size: 76px;
		line-height: 93px;
		color: var(--color-accent-interactive);
		text-shadow: 0px 2px 9px rgba(0, 0, 0, 0.25);
		@media (max-width: 900px) {
			display: none;
		}
	}
	h3 {
		
		width: 600px;
		height: 102px;
		left: ${({ left }) => `${left + 0}px`};
		top: ${({ offset }) => `${offset + 150}px`};

		font-family: ${MobergFontFamily.HEADER};
		font-style: normal;
		font-weight: 700;
		font-size: 32px;
		line-height: 39px;

		color: var(--color-text-primary);

		@media (max-width: 900px) {
			display: none;
		}
	}
	img {
		padding: 1rem;
		position: absolute;
		left: ${({ left }) => `${left - 10}px`};
		top: ${({ offset }) => `${offset + 47}px`};
		@media (max-width: 900px) {
			display: none;
		}
	}
	
`;


const ClinicalWrapper = styled.div`
	background-image: url(${bgImg});
	background-position: center;
	background-size: cover;
	background-repeat: no-repeat;
	background-color: var(--color-surface-primary);
	position: absolute;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	width: 100vw;
	height: 100vh;
	display: flex;
	align-items: center;
	justify-content: center;

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0);
		[data-color-scheme="dark"] & {
			background: rgba(0, 0, 0, 0.75);
		}
	}

	& > * {
		position: relative;
		z-index: 1;
	}
`;

const CarouselContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	margin-top: 32px;
	margin-bottom: 48px;
	height: auto;
	min-height: 550px;
	position: relative;
	width: 100%;
	
	/* Responsive adjustments to reduce gaps on larger screens */
	@media (min-width: 1440px) {
		min-height: 600px;
		margin-bottom: 64px;
	}
	
	@media (min-width: 1920px) {
		min-height: 650px;
		margin-bottom: 80px;
	}
	
	@media (min-width: 2560px) {
		min-height: 700px;
		margin-bottom: 96px;
	}
`;

const CenteredLoginBox = styled.div`
	z-index: 2;
	display: flex;
	align-items: center;
	justify-content: center;
	align-self: center;
	justify-self: center;
	height: auto;
	width: 100%;
	max-width: 450px;
	min-width: 400px;
	padding: 0 20px;
	margin: 0 auto;
	left: 0;
	top: 0;

	@media (max-width: 768px) {
		width: 90%;
		min-width: 280px;
		max-width: 400px;
	}

	@media (max-width: 480px) {
		width: 95%;
		min-width: 260px;
		max-width: 350px;
		padding: 0 10px;
	}
`;

const LoadingTitle = styled.h2`
    font-family: ${MobergFontFamily.HEADER};
    font-weight: 700;
    font-size: 20px;
    color: var(--color-text-primary);
    margin: 0;
    letter-spacing: 0.3px;
`;

const LoadingSubtitle = styled.p`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 14px;
    color: var(--text-secondary-color);
    margin: 0;
    line-height: 1.4;
`;

const LoadingWrapper = styled.div`
    background-color: var(--color-surface-primary);
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 24px;
`;



export default Login;

